import { flattenToPaths, partition, set } from '@exodus/basic-utils'
import { OrderSet, TxSet } from '@exodus/models'
import { resultFunc as getAssetSourceIncomingOrders } from '@exodus/core-selectors/orders/create-asset-source-incoming-orders.js'
import { resultFunc as getOrdersInProgress } from '@exodus/core-selectors/orders/orders-in-progress.js'
import { resultFunc as getPotentialCompleteOrders } from '@exodus/core-selectors/orders/potential-complete-orders.js'
import lodash from 'lodash'
import findOptimisticOrders from '@exodus/find-optimistic-orders'
import makeConcurrent, { byArguments as makeConcurrentByArguments } from 'make-concurrent'

import {
  convertLegacyOrderMetadata,
  isOrderMetadataValid,
  isOrderSupported,
  parseOrder,
} from './utils.js'

const { isEqual } = lodash

const legacyOrdersChannel = {
  type: 'order',
  channelName: 'orders',
  batchInterval: 1000,
}

const ordersChannel = {
  type: 'order',
  channelName: 'v1_orders',
  batchInterval: 1000,
}

const updateWithOrders = (set, orders) => {
  const [toUpdate, toAdd] = partition(orders, (order) => set.has(order))
  if (toUpdate.length > 0) set = set.update(toUpdate)
  if (toAdd.length > 0) set = set.addBulk(toAdd)

  return set
}

const findFailedOrders = ({ failedSentTxs, orders }) => {
  const result = []
  if (failedSentTxs.length > 0) {
    const failedSentTxsIds = new Set(failedSentTxs.map((tx) => tx.txId))
    orders.forEach((order) => {
      if (failedSentTxsIds.has(order.fromTxId)) {
        result.push(order.update({ status: 'failed-final' }))
      }
    })
  }

  return result
}

class OrdersModule {
  #logger
  #assetsModule
  #channel
  #fusion
  #loadedPromise
  #loadedResolve
  #loaded
  #optimisticActivityEnabled
  #ordersAtom
  #ordersMonitor
  #txLogsByAssetSource = Object.create(null)
  #txLogsAtom
  #subscriptions = []

  constructor({ assetsModule, fusion, logger, config, ordersAtom, ordersMonitor, txLogsAtom }) {
    this.#logger = logger
    this.#assetsModule = assetsModule
    this.#fusion = fusion
    this.#optimisticActivityEnabled = config.optimisticActivityEnabled
    this.#ordersAtom = ordersAtom
    this.#txLogsAtom = txLogsAtom

    this.#loadedPromise = new Promise((resolve) => {
      this.#loadedResolve = () => {
        this.#loaded = true
        resolve()
      }
    })

    this.#fusion.channel({
      ...legacyOrdersChannel,
      processBatch: this.#processBatch,
    })

    this.#channel = this.#fusion.channel({
      ...ordersChannel,
      processBatch: this.#processBatch,
    })

    this.#ordersMonitor = ordersMonitor
  }

  #handleOrderBulkUpdate = async (changes) => {
    const updatedOrders = changes.map(({ order, changes }) => order.update(changes))
    this.upsert(updatedOrders)
  }

  #subscribeOnTxLogUpdate = () =>
    this.#subscriptions.push(
      this.#txLogsAtom.observe(({ value, changes }) => {
        if (changes) {
          flattenToPaths(changes).forEach(([walletAccount, assetName, txLog]) =>
            this.#handleTxLogUpdate({ walletAccount, assetName, txLog })
          )
        } else {
          // txLogs is a Proxy.
          this.#txLogsByAssetSource = Object.fromEntries(Object.entries(value))
        }
      })
    )

  #handleTxLogUpdate = async ({ assetName, walletAccount, txLog }) => {
    if (this.#loaded) {
      await this.#updateOptimisticOrders({ txLog, assetName, walletAccount }).catch((e) => {
        this.#logger.warn('failed to update optimistic orders', e)
      })
    }

    set(this.#txLogsByAssetSource, [walletAccount, assetName], txLog)
  }

  #updateOptimisticOrders = makeConcurrentByArguments(
    async ({ txLog, walletAccount, assetName }) => {
      const orderSet = await this.#ordersAtom.get()
      const ordersInProgress = getOrdersInProgress(orderSet)
      const potentialCompleteOrders = getPotentialCompleteOrders(orderSet)

      const preTxs = this.#txLogsByAssetSource?.[walletAccount]?.[assetName] || TxSet.EMPTY

      let updatedOrders = []

      const relatedOrders = [...ordersInProgress, ...potentialCompleteOrders].filter(
        (order) =>
          order.toWalletAccount === walletAccount ||
          order.toAsset === assetName ||
          order.fromWalletAccount === walletAccount ||
          order.fromAsset === assetName
      )

      const failedSentTxs = [...txLog].filter((tx) => {
        const isNew = !preTxs.get(tx.txId)
        return !isNew && tx.failed && tx.sent
      })

      const incomingOrders = [
        ...getAssetSourceIncomingOrders({ orderSet: ordersInProgress, assetName, walletAccount }),
        ...getAssetSourceIncomingOrders({
          orderSet: potentialCompleteOrders,
          assetName,
          walletAccount,
        }),
      ].sort((a, b) => b.date - a.date)

      const optimisticUpdatedOrders = findOptimisticOrders({
        incomingOrders,
        preTxs,
        postTxs: txLog,
      })

      const failedOrders = findFailedOrders({ failedSentTxs, orders: relatedOrders })

      updatedOrders = [...updatedOrders, ...failedOrders, ...optimisticUpdatedOrders]

      if (updatedOrders.length > 0) {
        await this.upsert(updatedOrders)
      }
    },
    ({ assetName }) => assetName
  )

  #getChannel = async () => {
    await this.#channel.awaitProcessed()

    return this.#channel
  }

  #setOrders = async (orderSet) => {
    return this.#ordersAtom.set((currentValue) =>
      isEqual(currentValue, orderSet) ? currentValue : orderSet
    )
  }

  load = async () => {
    if (this.#optimisticActivityEnabled) {
      this.#subscribeOnTxLogUpdate()
    }

    this.#loadedResolve()

    this.#ordersMonitor.on('orders-bulk-update', this.#handleOrderBulkUpdate)
    this.#ordersMonitor.start()
  }

  #storeOrdersLocally = makeConcurrent(async (orders) => {
    const newOrders = updateWithOrders(await this.#ordersAtom.get(), orders)

    this.#logger.debug(`persisting ${newOrders.size} orders locally`)
    await this.#setOrders(newOrders)
  })

  upsert = makeConcurrent(async (orders) => {
    await this.#loadedPromise
    const { value: txLogsByAssetSource } = await this.#txLogsAtom.get()
    // make sure there are no received tx for order, otherwise mark it as optimistic complete.
    const updatedOrders = orders.map((order) => {
      if (
        this.#optimisticActivityEnabled &&
        order.toTxId &&
        !order.hasOptimisticStatus &&
        order.exodusStatus === 'in-progress'
      ) {
        const txLog = txLogsByAssetSource?.[order.toWalletAccount]?.[order.toAsset]

        if (txLog && txLog.get(order.toTxId)) {
          return order.update({
            status: 'optimistic-complete',
          })
        }
      }

      return order
    })

    await this.#storeOrdersLocally(updatedOrders)

    try {
      await this.#pushUnsyncedOrders()
    } catch (err) {
      this.#logger.error('failed to push unsynced orders to fusion', err)
    }
  })

  clear = async () => {
    await this.#ordersAtom.set(OrderSet.EMPTY)
    this.#logger.debug('cleared')
  }

  // Fusion development
  #pushUnsyncedOrders = async () => {
    await this.#loadedPromise
    const ordersSet = await this.#ordersAtom.get()
    const unsynced = [...ordersSet].filter((o) => !o.synced)

    if (unsynced.length === 0) return

    // Set orders as synced to prevent loop when pushing to fusion.
    await this.#storeOrdersLocally(unsynced.map((order) => order.update({ synced: true })))

    this.#logger.log(`pushing ${unsynced.length} orders to fusion`)
    return this.#pushToFusion(unsynced)
  }

  // Called whenever the fusion channel detects changes.
  #processBatch = async (ordersBatch) => {
    this.#logger.debug(`syncing down batch of ${ordersBatch.length} orders`)

    await this.#loadedPromise

    const orders = ordersBatch.map((orderData) => orderData.data)

    let assets = this.#assetsModule.getAssets()

    const parsedOrders = orders.map((order) => parseOrder({ order, assets, logger: this.#logger }))

    const unsupportedAssetNames = parsedOrders.reduce((acc, order) => {
      if (!assets[order.fromAsset]) {
        acc.add(order.fromAsset)
      }

      if (!assets[order.toAsset]) {
        acc.add(order.toAsset)
      }

      return acc
    }, new Set())

    if (unsupportedAssetNames.size > 0) {
      await this.#assetsModule
        .addRemoteTokens({ tokenNames: [...unsupportedAssetNames] })
        .catch((err) => {
          // If failed, the orders with unsupported assets will be removed from supportedOrders.
          this.#logger.error(
            `failed to add unavailable assets ${[...unsupportedAssetNames]} for orders`,
            err
          )
        })
    }

    assets = this.#assetsModule.getAssets()

    const supportedOrders = parsedOrders
      .filter((order) => order && isOrderSupported({ order, assets }))
      .map((order) => (isOrderMetadataValid(order) ? order : convertLegacyOrderMetadata(order)))

    const ordersSet = await this.#ordersAtom.get()
    const [existingOrders, unexistingOrders] = partition(supportedOrders, (order) =>
      ordersSet.has(order)
    )

    if (existingOrders.length > 0) {
      await this.#storeOrdersLocally(existingOrders.map((order) => order.update({ synced: true })))
    }

    const batchedOrders = unexistingOrders.map((order) =>
      order.update({ status: 'e2e-syncing', synced: true })
    )

    if (orders.length !== batchedOrders.length) {
      this.#logger.debug(
        `${batchedOrders.length}/${orders.length} orders were valid/supported on this platform`
      )
    }

    if (batchedOrders.length === 0) return

    await this.#storeOrdersLocally(batchedOrders)
    this.#ordersMonitor.tick()
  }

  #pushToFusion = async (orders) => {
    const channel = await this.#getChannel()

    const pushToFusionPromises = orders.map((order) =>
      channel.push({
        type: ordersChannel.type,
        data: order,
      })
    )

    await Promise.all(pushToFusionPromises)
    this.#logger.debug(`pushed ${orders.length} orders to fusion`)
  }

  stop = () => {
    this.#subscriptions.forEach((unsubscribe) => unsubscribe())
    this.#subscriptions = []
  }
}

const createOrdersModule = (...args) => new OrdersModule(...args)

const ordersModuleDefinition = {
  id: 'orders',
  type: 'module',
  factory: createOrdersModule,
  dependencies: [
    'assetsModule',
    'config',
    'fusion',
    'logger',
    'ordersAtom',
    'ordersMonitor',
    'txLogsAtom',
  ],
  public: true,
}

export default ordersModuleDefinition
